import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Textarea,
  ToggleButtonGroup,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { AddressContactDropdownDto } from '../../../api/created/addressContactDropdown.ts';
import {
  CreateAppointmentRequestDto,
  getAppointmentContact,
  getAppointmentDate,
  getAppointmentTimeFrom,
  getAppointmentTimeUntil,
  toGeneratedCreateAppointmentDto,
} from '../../../api/created/appointment.ts';
import {
  getGetAppointmentsQueryKey,
  useCreateAppointment,
  useUpdateAppointment,
} from '../../../api/generated/appointment-controller/appointment-controller.ts';
import {
  AppointmentDetailsDto,
  AppointmentDtoType,
  CreateAppointmentDtoType,
  GetPatientsBasicsType,
  PatientBasicsDto,
  UserDto,
} from '../../../api/generated/model';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../components/form/GermanDateInput.tsx';
import { getAppointmentTypeName } from '../../../utils/appointment.ts';
import { getDefaultValueForDateInput, getDefaultValueForTimeInput, isTimeAfter } from '../../../utils/date.ts';
import { SelectPatient } from '../SelectPatient.tsx';
import { SelectAppointmentExecutor } from './SelectAppointmentExecutor.tsx';

interface FormValues {
  patient: PatientBasicsDto | null;
  employee: AddressContactDropdownDto | null;
  appointmentType: CreateAppointmentDtoType | null;
  date: string;
  timeFrom: string;
  timeUntil: string;
  notes: string;
}

export function CreateAppointmentDialog({
  preselectedPatient,
  preselectedDate,
  open,
  onClose,
  currentAppointment,
}: {
  preselectedPatient?: PatientBasicsDto;
  preselectedDate?: { start: Date; end: Date };
  open: boolean;
  onClose: () => void;
  currentAppointment?: AppointmentDetailsDto;
}) {
  const isEditing = !!currentAppointment;
  const queryClient = useQueryClient();

  const {
    register,
    control,
    getValues,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(currentAppointment, preselectedPatient, preselectedDate),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const timeFrom = watch('timeFrom');

  const createAppointmentMutation = useCreateAppointment({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetAppointmentsQueryKey(),
        });
        handleOnClose();
      },
      onError: () => toast.error('Beim Erfassen des Termins ist ein Fehler aufgetreten'),
    },
  });

  const updateAppointmentMutation = useUpdateAppointment({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetAppointmentsQueryKey(),
        });
        handleOnClose();
      },
      onError: () => toast.error('Beim Bearbeiten des Termins ist ein Fehler aufgetreten'),
    },
  });

  const handleOnSubmit = (data: FormValues) => {
    const createAppointmentDto: CreateAppointmentRequestDto = {
      patientId: data.patient!.id,
      contactId: data.employee!.id!,
      note: data.notes,
      date: data.date,
      timeFrom: data.timeFrom || null,
      timeUntil: data.timeUntil || null,
      type: data.appointmentType!,
    };
    const generatedCreateAppointmentDto = toGeneratedCreateAppointmentDto(createAppointmentDto);

    if (isEditing) {
      updateAppointmentMutation.mutate({ id: currentAppointment.id, data: generatedCreateAppointmentDto });
    } else {
      createAppointmentMutation.mutate({ data: generatedCreateAppointmentDto });
    }
  };

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(currentAppointment, preselectedPatient, preselectedDate));
    }
  }, [open, preselectedDate, preselectedPatient, currentAppointment]);

  useEffect(() => {
    if (!timeFrom) {
      setValue('timeUntil', '');
    }
  }, [setValue, timeFrom]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 700 }}>
        <ModalClose />
        <Typography level={'h4'}>Termin {isEditing ? 'bearbeiten' : 'erstellen'}</Typography>
        <Divider />
        <form onSubmit={handleSubmit(handleOnSubmit)}>
          <Stack gap={2}>
            <Stack direction={'row'} gap={2}>
              <FormControl error={!!errors.date}>
                <FormLabel>
                  <Typography>Datum *</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="date"
                  rules={{
                    required: true,
                  }}
                />
                {errors.date && <FormHelperText>Datum muss ausgefüllt werden</FormHelperText>}
              </FormControl>
              <FormControl error={!!errors.timeFrom}>
                <FormLabel>
                  <Typography>Von</Typography>
                </FormLabel>
                <Input {...register('timeFrom')} type={'time'} />
              </FormControl>
              <FormControl error={!!errors.timeUntil}>
                <FormLabel>
                  <Typography>Bis</Typography>
                </FormLabel>
                <Input
                  {...register('timeUntil', {
                    validate: (value) => {
                      const timeFrom = getValues('timeFrom');

                      if (timeFrom && !value) return false;

                      return !value || isTimeAfter(timeFrom, value);
                    },
                  })}
                  type={'time'}
                  disabled={!timeFrom}
                />
                {errors.timeUntil && <FormHelperText>Bis muss nach &#39;Von&#39; sein</FormHelperText>}
              </FormControl>
            </Stack>
            <FormControl error={!!errors.patient}>
              <FormLabel>
                <Typography>Patient *</Typography>
              </FormLabel>
              {preselectedPatient ? (
                <Typography>
                  {preselectedPatient.lastName} {preselectedPatient.firstName}
                </Typography>
              ) : (
                <Controller
                  name={'patient'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <SelectPatient
                      onChange={onChange}
                      value={value}
                      patientsType={GetPatientsBasicsType.ALL}
                      disabled={isEditing}
                    />
                  )}
                />
              )}
              {errors.patient && <FormHelperText>Patient muss angegeben werden</FormHelperText>}
            </FormControl>
            <FormControl error={!!errors.appointmentType}>
              <FormLabel>
                <Typography>Termin-Typ *</Typography>
              </FormLabel>
              <Controller
                name={`appointmentType`}
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <ToggleButtonGroup value={value} disabled={isEditing}>
                    {Object.values(AppointmentDtoType).map((type) => (
                      <Button value={type} key={type} onClick={() => onChange(type)}>
                        {getAppointmentTypeName(type)}
                      </Button>
                    ))}
                  </ToggleButtonGroup>
                )}
              />
              {errors.appointmentType && <FormHelperText>Termin-Typ muss angegeben werden</FormHelperText>}
            </FormControl>

            <FormControl error={!!errors.employee}>
              <FormLabel>
                <Typography>Durchführung *</Typography>
              </FormLabel>

              <Controller
                name={'employee'}
                control={control}
                rules={{ validate: (value) => !!value?.id }}
                render={({ field: { onChange, value } }) => (
                  <SelectAppointmentExecutor value={value} onChange={onChange} disabled={isEditing} />
                )}
              />
              {errors.employee && <FormHelperText>Mitarbeiter muss angegeben werden</FormHelperText>}
            </FormControl>
            <FormControl>
              <FormLabel>
                <Typography>Bemerkungen</Typography>
              </FormLabel>
              <Textarea {...register('notes')} minRows={2} maxRows={4} />
            </FormControl>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={createAppointmentMutation.isPending || updateAppointmentMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

function getDefaultValues(
  currentAppointment?: AppointmentDetailsDto,
  preselectedPatient?: PatientBasicsDto,
  preselectedDate?: { start: Date; end: Date },
): FormValues {
  return {
    patient: currentAppointment?.patient ?? preselectedPatient ?? null,
    employee: mapUserToAppointmentExecutor(getAppointmentContact(currentAppointment)),
    appointmentType: currentAppointment?.type ?? null,
    date: getDefaultValueForDateInput(getAppointmentDate(currentAppointment) ?? undefined, preselectedDate?.start),
    timeFrom:
      getAppointmentTimeFrom(currentAppointment) ?? getDefaultValueForTimeInput(undefined, preselectedDate?.start),
    timeUntil:
      getAppointmentTimeUntil(currentAppointment) ?? getDefaultValueForTimeInput(undefined, preselectedDate?.end),
    notes: currentAppointment?.note ?? '',
  };
}

function mapUserToAppointmentExecutor(user?: UserDto | null): AddressContactDropdownDto | null {
  if (!user) return null;

  return {
    id: user.id,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    email: user.email,
    isActive: true,
    isPinned: false,
  };
}
